import REDFooter from "./REDFooter";
import { useState, useEffect, useRef, type ReactNode } from "react";
import {
  DBBrand,
  DBHeader,
  DBPage,
  DBSwitch,
  DBTooltip,
  DBNavigation,
  DBNavigationItem,
} from "@db-ux/react-core-components";
import { Outlet, useNavigate, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { supportedLanguages, type SupportedLanguage } from "../../i18n/config";
import REDLink from "./REDLink";
import REDPopover from "./REDPopover";
import REDBreadcrumb, { type BreadCrumbItem } from "./REDBreadcrumb";
import "./REDLayout.css";

// Type guard to check if a string is a supported language
function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return supportedLanguages.includes(lang as SupportedLanguage);
}

export interface NavigationItem {
  name: string;
  href: string;
  icon?: string;
}

export interface MetaNavLink {
  translationKey: string; // i18n key like 'common:helpContact'
  href: string; // Link URL
  target?: "_blank" | "_self";
  icon?: string; // Optional DB icon
}

interface LayoutProps {
  children: ReactNode;
  isAuthenticated?: boolean;
  onLogout?: () => void;
  pageMaxWidth1280?: boolean;
  breadCrumbData?: BreadCrumbItem[];
  hasOverlayImage?: boolean;
  headerImg?: string;
  headerImgAlt?: string; // Alt text for header image
  imageCopyright?: string;
  brandName?: string;
  primaryNavigation?: NavigationItem[];
  showLanguageSwitcher?: boolean; // Default: true
  showMetaNavigation?: boolean; // Default: true
  metaNavigationLinks?: MetaNavLink[]; // Custom links array
  blueBusinessMetaNavigation?: boolean; // Default: false - Applies blue business background to meta navigation
}

export const REDLayout: React.FC<LayoutProps> = ({
  children,
  pageMaxWidth1280 = false,
  breadCrumbData = [],
  hasOverlayImage = false,
  headerImg,
  headerImgAlt = "Header",
  imageCopyright,
  brandName,
  primaryNavigation,
  showLanguageSwitcher = true,
  showMetaNavigation = true,
  metaNavigationLinks = [],
  blueBusinessMetaNavigation = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useParams<{ locale?: string }>();

  // supportedLanguages is imported from i18n/config (static list)

  // Initialize theme from browser preference or localStorage
  const getInitialTheme = () => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";

    // Check browser preference
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  };

  const [isDarkMode, setIsDarkMode] = useState<boolean>(getInitialTheme);

  // Update DOM and localStorage when theme changes
  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.setAttribute("data-mode", isDarkMode ? "dark" : "light");
    }
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Sync locale from URL params with i18n
  useEffect(() => {
    if (locale && isSupportedLanguage(locale)) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if no preference is stored
      if (!localStorage.getItem("theme")) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const handleThemeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Helper function to check if navigation item is active
  const isActive = (itemHref: string, currentPath: string) => {
    // Normalize paths (remove trailing slashes)
    const normalizedPath = currentPath.replace(/\/$/, "");
    const normalizedHref = itemHref.replace(/\/$/, "");

    // Exact match
    return normalizedPath === normalizedHref;
  };

  // Language names in their native language - Extended for 6 languages
  const languageNames: Record<string, string> = {
    de: "Deutsch",
    en: "English",
    fr: "Français",
    it: "Italiano",
    es: "Español",
    pl: "Polski",
  };

  // Get current language name for label
  const currentLanguageName =
    languageNames[i18n.language as SupportedLanguage] ||
    i18n.language.toUpperCase();

  // Language selector options - exclude current language
  const languageOptions = supportedLanguages
    .filter((lang) => lang !== i18n.language)
    .map((lang) => ({
      value: lang,
      label: languageNames[lang as SupportedLanguage],
    }));

  const handleLanguageChange = async (newLang: string) => {
    if (isSupportedLanguage(newLang)) {
      // Change i18n language
      await i18n.changeLanguage(newLang);

      // Update URL to reflect new language
      const currentPath = location.pathname;
      const pathSegments = currentPath.split("/").filter(Boolean);

      // Replace the language segment or add it if missing
      if (pathSegments[0] && isSupportedLanguage(pathSegments[0])) {
        pathSegments[0] = newLang;
      } else {
        pathSegments.unshift(newLang);
      }

      const newPath = "/" + pathSegments.join("/");
      navigate(newPath);
    }
    setOpenLanguageMenu(false);
    setDrawerOpen(false);
  };

  const [openLanguageMenu, setOpenLanguageMenu] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (drawerOpen) return;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpenLanguageMenu(false);
      }
    }

    if (openLanguageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openLanguageMenu, drawerOpen]);

  return (
    <div className="db-bg-color-basic-level-2">
      <div className={`${pageMaxWidth1280 ? `mx-auto overflow-x-hidden` : ""}`}>
        <DBPage
          variant="auto"
          documentOverflow="auto"
          fadeIn={true}
          mainClass={`${pageMaxWidth1280 ? `max-w-[1200px] mx-auto` : ""} ${
            hasOverlayImage ? "md:pt-0" : "py-4"
          }`}
          header={
            <>
              <DBHeader
                className={`border-none shadow-[0_2px_8px_#0000004d] ${
                  pageMaxWidth1280 ? `max-w-[1200px] mx-auto` : ""
                } justify-center z-20 relative`}
                data-blue-business={blueBusinessMetaNavigation}
                metaNavigation={
                  <div className="flex items-center gap-4">
                    {/* Meta Navigation Links */}
                    {showMetaNavigation &&
                      metaNavigationLinks &&
                      metaNavigationLinks.length > 0 &&
                      metaNavigationLinks.map((link, index) => (
                        <REDLink
                          key={index}
                          className={`hover:underline text-[12px] leading-none pr-2 inline-flex items-center ${
                            blueBusinessMetaNavigation ? "text-white" : ""
                          }`}
                          noDefaultUnderline
                          variant="inline"
                          target={link.target || "_self"}
                          href={link.href}
                        >
                          {link.icon && (
                            <span data-icon={link.icon} className={`mr-1`}></span>
                          )}
                          {t(link.translationKey)}
                        </REDLink>
                      ))}

                    {/* Language Switcher */}
                    {showLanguageSwitcher && (
                      <REDPopover
                        className="header-language-menu"
                        delay="none"
                        animation={false}
                        ref={popoverRef}
                        open={openLanguageMenu}
                        trigger={
                          <button
                            type="button"
                            onClick={() =>
                              setOpenLanguageMenu(!openLanguageMenu)
                            }
                            className="text-left flex items-center group"
                          >
                            <span className={`group-hover:underline text-[12px] leading-none ${blueBusinessMetaNavigation ? "text-white" : ""}`}>
                              {currentLanguageName}
                            </span>{" "}
                            <span
                              className={`pl-2 group-hover:no-underline text-[12px] leading-none ${
                                openLanguageMenu ? "text-[#c50014]" : ""
                              } ${blueBusinessMetaNavigation ? "text-white" : ""}`}
                              data-icon={
                                openLanguageMenu ? "chevron_up" : "chevron_down"
                              }
                            ></span>
                          </button>
                        }
                        placement="bottom"
                        spacing="small"
                      >
                        <ul className="flex flex-col gap-1 pl-0 pt-2 pb-2 px-2 list-none">
                          {languageOptions
                            .filter((option) => option.value !== i18n.language)
                            .map((option) => (
                              <li key={option.value}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleLanguageChange(option.value)
                                  }
                                  className="w-full text-left py-1 min-w-70px flex items-center group"
                                >
                                  <span data-icon="chevron_right"></span>{" "}
                                  <span className={`group-hover:underline`}>
                                    {option.label}
                                  </span>
                                </button>
                              </li>
                            ))}
                        </ul>
                      </REDPopover>
                    )}
                  </div>
                }
                drawerOpen={drawerOpen}
                onToggle={setDrawerOpen}
                brand={
                  <DBBrand showIcon={false} data-density="expressive">
                    <img
                      className="h-7 md:h-8 lg:h-10 w-auto"
                      alt="Home"
                      src="/db-logo.svg"
                    ></img>
                    {brandName}
                  </DBBrand>
                }
                secondaryAction={
                  <DBSwitch
                    visualAid
                    icon="sun"
                    iconTrailing={"moon"}
                    checked={isDarkMode}
                    onChange={handleThemeToggle}
                  >
                    <DBTooltip>{t('common:themeSwitchTooltip')}</DBTooltip>
                  </DBSwitch>
                }
              >
                {primaryNavigation && (
                  <DBNavigation>
                    {primaryNavigation.map((item) => (
                      <DBNavigationItem
                        className="mx-4 cursor-pointer"
                        key={item.href}
                        active={isActive(item.href, location.pathname)}
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          navigate(item.href);
                        }}
                      >
                        <span data-icon={item.icon}>{item.name}</span>
                      </DBNavigationItem>
                    ))}
                  </DBNavigation>
                )}
              </DBHeader>
              {hasOverlayImage && headerImg && (
                <div className="relative w-[1280px] max-w-full mx-auto -mt-[112px] z-0">
                  <img
                    src={headerImg}
                    className="xl:rounded-b-[8px] object-cover w-full h-[65vh] max-h-[420px] min-h-[375px] md:h-[400px] md:max-h-none md:min-h-0"
                    alt={headerImgAlt}
                  />
                  {imageCopyright && (
                    <div className="absolute bottom-0 right-0 z-10 bg-black/40 p-0.5 xl:rounded-br-[8px]">
                      <span
                        className="text-white text-xs md:text-sm opacity-80 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] font-medium"
                        style={{
                          writingMode: "vertical-rl",
                          textOrientation: "mixed",
                        }}
                      >
                        © {imageCopyright}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          }
        >
          {breadCrumbData && breadCrumbData.length > 0 && (
            <REDBreadcrumb breadCrumbData={breadCrumbData} />
          )}

          {children}

          <Outlet />
        </DBPage>
      </div>
      <REDFooter />
    </div>
  );
};
